import { useMemo, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  useFirestore,
  useFirestoreGeneral,
  useFirestoreNoCompagnyGeneralOrderBy,
} from "../../firebase/useFirestore";
import { client } from "../../hooks/Client";
import CompagnyMeta from "../../components/portfolio/CompagnyMeta";
import EffectMeta from "../../components/effects/EffectMeta";
import OutputMeta from "../../components/outputs/OutputMeta";
import FieldMeta from "../../components/Questionnaire/FieldMeta";
import TargetgroupMeta from "../../components/stakeholders/TargetgroupMeta";
import CustomEventFieldMeta from "../../components/events/CustomEventFieldMeta";
import EventFieldMeta from "../../components/events/EventFieldMeta";
import Tooltip from "../../components/common/Tooltip";
import DeleteModal from "../../components/common/DeleteModal";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import SelectableGrid from "../../components/common/SelectableGrid.jsx";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  createCustomEventFieldFromSynchronisation,
  createEffectFromSynchronisation,
  createEventFieldFromSynchronisation,
  createOutputFromSynchronisation,
  createQuestionFromSynchronisation,
  createTargetgroupFromSynchronisation,
  deleteSynchronisation,
  pairExistingMSIEffect,
  pairProjectCustomEventField,
  pairProjectEventField,
  pairProjectOutput,
  pairProjectTargetgroup,
  resetPairedSynchronisation,
  updateSynchronisationStatus,
} from "../../components/synchronisations/synchronisationActions";
import {
  synchronisationStatus,
  synchronisationType,
} from "../../components/synchronisations/synchronisationHelpers";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";

const STATUS_OPTIONS = ["requested", "accepted", "paired", "declined", "deleted"];

const Synchronisations = () => {
  // State
  const [loadingSyncId, setLoadingSyncId] = useState("");
  const [activityIds, setActivityIds] = useState({});
  const [effectIds, setEffectIds] = useState({});
  const [pairMode, setPairMode] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("requested");

  // Firestore
  const syncs = useFirestoreNoCompagnyGeneralOrderBy(
    "Synchronisations",
    "Project",
    client,
    "Timestamp",
    "desc"
  );
  const outputs = useFirestore("Outputs");
  const activities = useFirestore("Activities");
  const effects = useFirestore("OutputEffects");
  const targetgroups = useFirestore("Stakeholders");
  const MSIEffects = useFirestoreGeneral("OutputEffects", "Standard", "true");
  const customEventFields = useFirestore("CustomEventFields");
  const eventFields = useFirestore("EventFields");

  // `outputs` and `date` event fields are not syncable as activity fields —
  // outputs sync via the Outputs collection, date fields carry no data.
  const syncableEventFields = useMemo(
    () =>
      (eventFields || []).filter(
        (field) => field.Type !== "outputs" && field.Type !== "date"
      ),
    [eventFields]
  );

  // Lookups
  const activityNameById = useMemo(() => {
    const map = {};
    (activities || []).forEach((activity) => {
      map[activity.ID] = activity.Activity;
    });
    return map;
  }, [activities]);

  const formatOutputLabel = (output) => {
    const activityName = activityNameById[output.ActivityID];
    return `${output.Title} (${activityName || "geen activiteit"})`;
  };

  const formatEffectLabel = (effect) => {
    const ids = Array.isArray(effect.Activity) ? effect.Activity : [];
    const names = ids
      .map((id) => activityNameById[id])
      .filter(Boolean);
    const context = names.length ? names.join(", ") : "geen activiteit";
    return `${effect.Effect} (${context})`;
  };

  const setPairModeFor = (syncId, mode) => {
    setPairMode((current) => ({ ...current, [syncId]: mode }));
  };

  const setLoadingState = (syncId) => {
    setLoadingSyncId(syncId);
  };

  const clearLoadingState = () => {
    setLoadingSyncId("");
  };

  const activityChangeHandler = (syncId, value) => {
    setActivityIds((currentActivityIds) => ({
      ...currentActivityIds,
      [syncId]: value,
    }));
  };

  const effectChangeHandler = (syncId, value) => {
    setEffectIds((currentEffectIds) => ({
      ...currentEffectIds,
      [syncId]: value,
    }));
  };

  const statusChangeHandler = async (docid, syncId, status) => {
    setLoadingState(syncId);
    try {
      await updateSynchronisationStatus(docid, status);
    } finally {
      clearLoadingState();
    }
  };

  const projectOutputHandler = async (docid, projectOutputId, syncId) => {
    if (!projectOutputId) return;

    setLoadingState(syncId);
    try {
      await pairProjectOutput(docid, projectOutputId);
    } finally {
      clearLoadingState();
    }
  };

  const createNewOutput = async (sync, syncId) => {
    const activityId = activityIds[syncId];

    if (!sync.Output || !sync.docid || !activityId) return;

    setLoadingState(syncId);
    try {
      await createOutputFromSynchronisation(
        sync,
        activityId,
        outputs?.length || 0
      );
    } finally {
      clearLoadingState();
    }
  };

  const projectEffectHandler = async (sync) => {
    if (!sync.docid || !sync.Effect || !sync.ID) return;

    setLoadingState(sync.ID);
    try {
      await createEffectFromSynchronisation(sync);
    } finally {
      clearLoadingState();
    }
  };

  const pairToExistingMSIEffect = async (sync, projectEffectId) => {
    if (!projectEffectId || !sync.docid) return;

    setLoadingState(sync.ID);
    try {
      await pairExistingMSIEffect(sync, projectEffectId);
    } finally {
      clearLoadingState();
    }
  };

  const createNewQuestion = async (sync, syncId) => {
    const effectId = effectIds[syncId];

    if (!sync.docid || !sync.Question || !effectId) return;

    setLoadingState(syncId);
    try {
      await createQuestionFromSynchronisation(sync, effectId);
    } finally {
      clearLoadingState();
    }
  };

  const editPairedSync = async (docid, syncId) => {
    setLoadingState(syncId);
    try {
      await resetPairedSynchronisation(docid);
    } finally {
      clearLoadingState();
    }
  };

  const deleteModalHandler = (event) => {
    const docid = event.currentTarget.dataset.docid;
    const name = event.currentTarget.dataset.deletename;

    setDeleteDocid(docid);
    setDeleteName(name);
    setDeleteModal(true);
  };

  const updateSync = async () => {
    await deleteSynchronisation(deleteDocid);
    setDeleteDocid("");
    setDeleteModal(false);
  };

  const projectTargetgroupHandler = async (sync, projectTargetgroupId) => {
    if (!projectTargetgroupId) return;

    setLoadingState(sync.ID);
    try {
      await pairProjectTargetgroup(sync, projectTargetgroupId);
    } finally {
      clearLoadingState();
    }
  };

  const createNewTargetgroup = async (sync) => {
    if (!sync.docid || !sync.Targetgroup) return;

    setLoadingState(sync.ID);
    try {
      await createTargetgroupFromSynchronisation(sync, targetgroups?.length || 0);
    } finally {
      clearLoadingState();
    }
  };

  const projectCustomEventFieldHandler = async (sync, fieldId) => {
    if (!fieldId) return;

    setLoadingState(sync.ID);
    try {
      await pairProjectCustomEventField(sync, fieldId);
    } finally {
      clearLoadingState();
    }
  };

  const createNewCustomEventField = async (sync) => {
    if (!sync.docid || !sync.CustomEvent) return;

    setLoadingState(sync.ID);
    try {
      await createCustomEventFieldFromSynchronisation(
        sync,
        customEventFields?.length || 0
      );
    } finally {
      clearLoadingState();
    }
  };

  const projectEventFieldHandler = async (sync, fieldId) => {
    if (!fieldId) return;

    setLoadingState(sync.ID);
    try {
      await pairProjectEventField(sync, fieldId);
    } finally {
      clearLoadingState();
    }
  };

  const createNewEventField = async (sync, syncId) => {
    const activityId = activityIds[syncId];

    if (!sync.docid || !sync.Event || !activityId) return;

    setLoadingState(syncId);
    try {
      await createEventFieldFromSynchronisation(sync, activityId);
    } finally {
      clearLoadingState();
    }
  };

  const renderSyncName = (sync) => {
    if (sync.Type === "Output") return <OutputMeta id={sync.Output} />;
    if (sync.Type === "Effect") return <EffectMeta item={sync.Effect} />;
    if (sync.Type === "MSIEffect") return <EffectMeta item={sync.Effect} />;
    if (sync.Type === "Question") return <FieldMeta item={sync.Question} />;
    if (sync.Type === "Targetgroup") {
      return <TargetgroupMeta targetgroupID={sync.Targetgroup} />;
    }
    if (sync.Type === "CustomEvent") {
      return <CustomEventFieldMeta fieldId={sync.CustomEvent} />;
    }
    if (sync.Type === "Event") {
      return <EventFieldMeta fieldId={sync.Event} />;
    }

    return null;
  };

  const renderPairedName = (sync) => {
    if (sync.Type === "Output" && sync.ProjectOutput) {
      const output = (outputs || []).find(
        (item) => item.ID === sync.ProjectOutput
      );
      if (output) {
        return <>{formatOutputLabel(output)}</>;
      }
      return <OutputMeta id={sync.ProjectOutput} />;
    }

    if (
      (sync.Type === "Effect" || sync.Type === "MSIEffect") &&
      sync.ProjectEffect
    ) {
      const effect = (effects || []).find(
        (item) => item.ID === sync.ProjectEffect
      );
      if (effect) {
        return <>{formatEffectLabel(effect)}</>;
      }
      return <EffectMeta item={sync.ProjectEffect} />;
    }

    if (sync.Type === "Question" && sync.ProjectQuestion) {
      return <FieldMeta item={sync.ProjectQuestion} />;
    }

    if (sync.Type === "Targetgroup" && sync.ProjectTargetgroup) {
      return <TargetgroupMeta targetgroupID={sync.ProjectTargetgroup} />;
    }

    if (sync.Type === "CustomEvent" && sync.ProjectCustomEventField) {
      return <CustomEventFieldMeta fieldId={sync.ProjectCustomEventField} />;
    }

    if (sync.Type === "Event" && sync.ProjectEvent) {
      return <EventFieldMeta fieldId={sync.ProjectEvent} />;
    }

    return <span className="sync-table-empty">-</span>;
  };

  const renderExistingPairCell = (sync) => {
    if (sync.Status !== "accepted") {
      return <span className="sync-table-empty">-</span>;
    }

    if (sync.Type === "Output") {
      return (
        <select
          disabled={loadingSyncId === sync.ID}
          value={sync.ProjectOutput || ""}
          onChange={(event) =>
            projectOutputHandler(sync.docid, event.target.value, sync.ID)
          }
        >
          <option value="">-- Selecteer een output --</option>
          {outputs &&
            outputs.map((output) => (
              <option key={output.ID} value={output.ID}>
                {formatOutputLabel(output)}
              </option>
            ))}
        </select>
      );
    }

    if (sync.Type === "MSIEffect") {
      return (
        <select
          disabled={loadingSyncId === sync.ID}
          value={sync.ProjectEffect || ""}
          onChange={(event) =>
            pairToExistingMSIEffect(sync, event.target.value)
          }
        >
          <option value="">-- Selecteer een Meetstandaard effect --</option>
          {MSIEffects &&
            MSIEffects.map((effect) => (
              <option key={effect.ID} value={effect.ID}>
                {effect.Effect}
              </option>
            ))}
        </select>
      );
    }

    if (sync.Type === "Targetgroup") {
      return (
        <select
          disabled={loadingSyncId === sync.ID}
          value={sync.ProjectTargetgroup || ""}
          onChange={(event) =>
            projectTargetgroupHandler(sync, event.target.value)
          }
        >
          <option value="">-- Selecteer een doelgroep --</option>
          {targetgroups &&
            targetgroups.map((group) => (
              <option key={group.ID} value={group.ID}>
                {group.Organisation}
              </option>
            ))}
        </select>
      );
    }

    if (sync.Type === "CustomEvent") {
      return (
        <select
          disabled={loadingSyncId === sync.ID}
          value={sync.ProjectCustomEventField || ""}
          onChange={(event) =>
            projectCustomEventFieldHandler(sync, event.target.value)
          }
        >
          <option value="">-- Selecteer een custom veld --</option>
          {customEventFields &&
            customEventFields.map((field) => (
              <option key={field.ID} value={field.ID}>
                {field.Title}
              </option>
            ))}
        </select>
      );
    }

    if (sync.Type === "Event") {
      return (
        <select
          disabled={loadingSyncId === sync.ID}
          value={sync.ProjectEvent || ""}
          onChange={(event) =>
            projectEventFieldHandler(sync, event.target.value)
          }
        >
          <option value="">-- Selecteer een activiteitveld --</option>
          {syncableEventFields.map((field) => (
            <option key={field.ID} value={field.ID}>
              {field.Title || "(zonder titel)"}
            </option>
          ))}
        </select>
      );
    }

    return <span className="sync-table-empty">-</span>;
  };

  const renderCreateCell = (sync) => {
    if (sync.Status !== "accepted") {
      return <span className="sync-table-empty">-</span>;
    }

    if (sync.Type === "Output") {
      return (
        <div className="sync-table-cell-actions">
          <select
            disabled={loadingSyncId === sync.ID}
            value={activityIds[sync.ID] || ""}
            onChange={(event) =>
              activityChangeHandler(sync.ID, event.target.value)
            }
          >
            <option value="">-- Selecteer een activiteit --</option>
            {activities &&
              activities.map((activity) => (
                <option key={activity.ID} value={activity.ID}>
                  {activity.Activity}
                </option>
              ))}
          </select>
          <button
            disabled={!activityIds[sync.ID] || loadingSyncId === sync.ID}
            onClick={() => createNewOutput(sync, sync.ID)}
          >
            Creëer output
          </button>
        </div>
      );
    }

    if (sync.Type === "MSIEffect") {
      return (
        <button
          disabled={loadingSyncId === sync.ID}
          onClick={() => projectEffectHandler(sync)}
        >
          Voeg effect toe
        </button>
      );
    }

    if (sync.Type === "Question") {
      return (
        <div className="sync-table-cell-actions">
          <select
            disabled={loadingSyncId === sync.ID}
            value={effectIds[sync.ID] || ""}
            onChange={(event) =>
              effectChangeHandler(sync.ID, event.target.value)
            }
          >
            <option value="">-- Selecteer een effect --</option>
            {effects &&
              effects.map((effect) => (
                <option key={effect.ID} value={effect.ID}>
                  {formatEffectLabel(effect)}
                </option>
              ))}
          </select>
          <button
            disabled={!effectIds[sync.ID] || loadingSyncId === sync.ID}
            onClick={() => createNewQuestion(sync, sync.ID)}
          >
            Creëer vraag
          </button>
        </div>
      );
    }

    if (sync.Type === "Targetgroup") {
      return (
        <button
          disabled={loadingSyncId === sync.ID}
          onClick={() => createNewTargetgroup(sync)}
        >
          Creëer doelgroep
        </button>
      );
    }

    if (sync.Type === "CustomEvent") {
      return (
        <button
          disabled={loadingSyncId === sync.ID}
          onClick={() => createNewCustomEventField(sync)}
        >
          Creëer custom veld
        </button>
      );
    }

    if (sync.Type === "Event") {
      return (
        <div className="sync-table-cell-actions">
          <select
            disabled={loadingSyncId === sync.ID}
            value={activityIds[sync.ID] || ""}
            onChange={(event) =>
              activityChangeHandler(sync.ID, event.target.value)
            }
          >
            <option value="">-- Selecteer een activiteit --</option>
            {activities &&
              activities.map((activity) => (
                <option key={activity.ID} value={activity.ID}>
                  {activity.Activity}
                </option>
              ))}
          </select>
          <button
            disabled={!activityIds[sync.ID] || loadingSyncId === sync.ID}
            onClick={() => createNewEventField(sync, sync.ID)}
          >
            Creëer veld
          </button>
        </div>
      );
    }

    return <span className="sync-table-empty">-</span>;
  };

  const renderPairingCell = (sync) => {
    if (sync.Status !== "accepted") {
      return <span className="sync-table-empty">-</span>;
    }

    // Question only has a "create new" path, no existing-pair option.
    if (sync.Type === "Question") {
      return renderCreateCell(sync);
    }

    // Effect type has neither a pair-existing nor a create-new path here.
    if (sync.Type === "Effect") {
      return <span className="sync-table-empty">-</span>;
    }

    // Output, MSIEffect, Targetgroup, CustomEvent, Event — both paths, radio toggle.
    const mode = pairMode[sync.ID];
    const radioName = `pair-mode-${sync.ID}`;
    const disabled = loadingSyncId === sync.ID;

    return (
      <div className="sync-pair-mode-container">
        <div className="sync-pair-mode-radios">
          <label>
            <input
              type="radio"
              name={radioName}
              value="existing"
              checked={mode === "existing"}
              disabled={disabled}
              onChange={() => setPairModeFor(sync.ID, "existing")}
            />
            Koppel aan bestaand
          </label>
          <label>
            <input
              type="radio"
              name={radioName}
              value="create"
              checked={mode === "create"}
              disabled={disabled}
              onChange={() => setPairModeFor(sync.ID, "create")}
            />
            Creëer nieuw
          </label>
        </div>
        {mode && (
          <div className="sync-pair-mode-body">
            {mode === "existing"
              ? renderExistingPairCell(sync)
              : renderCreateCell(sync)}
          </div>
        )}
      </div>
    );
  };

  const renderStatusCell = (sync) => {
    const statusMeta = synchronisationStatus(sync.Status);

    if (sync.Status === "paired") {
      return (
        <>
          <span className={`${statusMeta.className} sync-status-paired-label`}>
            {statusMeta.text}
          </span>
          {loadingSyncId === sync.ID && (
            <p className="sync-inline-loading">Bezig...</p>
          )}
        </>
      );
    }

    return (
      <>
        <select
          className={statusMeta.className}
          disabled={loadingSyncId === sync.ID}
          value={sync.Status || ""}
          onChange={(event) =>
            statusChangeHandler(sync.docid, sync.ID, event.target.value)
          }
        >
          <option value="requested">Aangevraagd</option>
          <option value="accepted">Geaccepteerd</option>
          <option value="declined">Geweigerd</option>
          <option value="deleted">Verwijderd</option>
        </select>
        {loadingSyncId === sync.ID && (
          <p className="sync-inline-loading">Bezig...</p>
        )}
      </>
    );
  };

  const renderManageCell = (sync, canEdit) => {
    if (!canEdit) {
      return <span className="sync-table-empty">-</span>;
    }

    if (sync.Status === "paired") {
      return (
        <div className="sync-table-cell-actions">
          <Tooltip content={"Synchronisatie bewerken"} width="100%" top="-60px">
            <EditOutlinedIcon
              className="table-delete-icon"
              onClick={() => editPairedSync(sync.docid, sync.ID)}
            />
          </Tooltip>
          <Tooltip content={"Synchronisatie verwijderen"} width="100%" top="-60px">
            <DeleteOutlinedIcon
              className="table-delete-icon"
              data-docid={sync.docid}
              data-deletename="Synchronisatie"
              onClick={deleteModalHandler}
            />
          </Tooltip>
        </div>
      );
    }

    if (sync.Status === "accepted") {
      return (
        <Tooltip content={"Synchronisatie verwijderen"} width="100%" top="-60px">
          <DeleteOutlinedIcon
            className="table-delete-icon"
            data-docid={sync.docid}
            data-deletename="Synchronisatie"
            onClick={deleteModalHandler}
          />
        </Tooltip>
      );
    }

    return <span className="sync-table-empty">-</span>;
  };

  const statusItems = useMemo(
    () =>
      STATUS_OPTIONS.map((code) => ({
        id: code,
        label: synchronisationStatus(code).text,
        count: (syncs || []).filter((sync) => sync.Status === code).length,
      })),
    [syncs]
  );

  const filteredSyncs = useMemo(
    () => (syncs || []).filter((sync) => sync.Status === selectedStatus),
    [syncs, selectedStatus]
  );

  const tableColumns = [
    { key: "organisation", label: "ORGANISATIE", searchable: false },
    { key: "type", label: "TYPE", searchable: false },
    { key: "name", label: "NAAM", searchable: false },
    { key: "status", label: "STATUS", searchable: false },
    { key: "pairing", label: "KOPPELING", searchable: false },
    { key: "paired", label: "GEKOPPELD", searchable: false },
    { key: "manage", label: "VERWIJDEREN", searchable: false },
  ];

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <h1>Koppelingen</h1>
        </div>
        <div className="msi-sector-grid-container">
          <SelectableGrid
            items={statusItems}
            getId={(item) => item.id}
            getLabel={(item) => item.label}
            selectedId={selectedStatus}
            onClick={(id) => setSelectedStatus(id)}
          />
        </div>
        <ReadOnlyGuard edit="reports.edit">
          {(canEdit) => (
            <div className="dashboard-container">
              <Table
                columns={tableColumns}
                items={filteredSyncs}
                getItemKey={(sync) => sync.ID}
                showSearch={false}
                emptyMessage="Geen koppelingen gevonden."
                renderRow={(sync) => (
                  <>
                    <TableCell>
                      <CompagnyMeta id={sync.Parent} />
                    </TableCell>
                    <TableCell>{synchronisationType(sync.Type)}</TableCell>
                    <TableCell>{renderSyncName(sync)}</TableCell>
                    <TableCell>{renderStatusCell(sync)}</TableCell>
                    <TableCell>{renderPairingCell(sync)}</TableCell>
                    <TableCell>{renderPairedName(sync)}</TableCell>
                    <TableCell>{renderManageCell(sync, canEdit)}</TableCell>
                  </>
                )}
              />
            </div>
          )}
        </ReadOnlyGuard>
      </div>

      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={updateSync}
        deleteName={deleteName}
      />
    </div>
  );
};

export default Synchronisations;
